// Inside Index.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { Box, Container, Grid, Typography, Avatar } from '@mui/material';
import { useLocation, useParams, NavLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getBlogs } from '../../apiCalls/urlFunction';
import { titleToUrl } from '../../shared/extra';
import PageBreadcrumb from '../../components/other/PageBreadcrumb';
import PageLoading from '../../PageLoading';
import moment from 'moment';

const Index = ({ isMobile = false }) => {
    const [blogData, setBlogData] = useState([]);
    const [currentBlog, setCurrentBlog] = useState(null);
    const [loading, setLoading] = useState(true);

    const { title } = useParams();
    const location = useLocation();

    const canonicalUrl = useMemo(
        () => `https://www.99gift.ae${location.pathname}`,
        [location.pathname]
    );

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                setLoading(true);
                const blogResponse = await fetch('/blogs.json');
                const json = await blogResponse.json();
                setBlogData(json?.data);
                const matchedBlog = json?.data?.find((blog) => titleToUrl(blog?.title) === title);
                setCurrentBlog(matchedBlog);
            } catch (err) {
                console.error('Failed to load blog');
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, [title]);

    if (loading) return <PageLoading />;




    return (
        <React.Fragment>
            {/* ---------- SEO Meta ---------- */}
            <Helmet>
                <title>{currentBlog?.metaSEO?.metaTitle || currentBlog?.title}</title>
                <meta name="description" content={currentBlog?.metaSEO?.metaDescription} />
                <meta property="og:title" content={currentBlog?.metaSEO?.metaTitle || currentBlog?.title} />
                <meta property="og:url" content={canonicalUrl} />
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>

            {/* ---------- Breadcrumb ---------- */}
            <Container maxWidth="xl">
                {!isMobile && (
                    <PageBreadcrumb home="Home" other="Blog" currentProduct={currentBlog?.title} />
                )}
            </Container>

            {/* ---------- Blog Section ---------- */}
            <Container maxWidth="md" sx={{ mt: 3, mb: 12 }}>
                {/* Published date + Category */}
                <Typography sx={{ color: '#777', fontSize: '14px' }}>
                    Published {moment(currentBlog?.created_at).format('MMMM D, YYYY')} in{' '}
                    <NavLink to={`/blogs?category=${currentBlog?.category}`} style={{ color: '#007bff' }}>
                        {currentBlog?.category}
                    </NavLink>
                </Typography>

                {/* Title */}
                <Typography
                    variant="h1"
                    sx={{
                        fontSize: { xs: '1.8rem', md: '3.6rem' },
                        fontWeight: 700,
                        mt: 1,
                        mb: 1,
                        lineHeight: 1.2,
                    }}
                >
                    {currentBlog?.title}
                </Typography>

                {/* Author Section */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    {/* Avatar + Name */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: '#000' }}>A</Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 600 }}>99gift</Typography>
                            <Typography sx={{ fontSize: '13px', color: '#888' }}>Marketing</Typography>
                        </Box>
                    </Box>

                    {/* Social Icons */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            ml: { xs: 60, md: 75 },
                        }}
                    >
                        {/* Instagram */}
                        <a
                            href="https://www.instagram.com/99gift.ae"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <Box
                                component="img"
                                src="https://cdn-icons-png.flaticon.com/512/2111/2111463.png"
                                alt="Instagram"
                                sx={{ width: 22, height: 22, cursor: 'pointer' }}
                            />
                        </a>

                        {/* Facebook */}
                        <a
                            href="https://www.facebook.com/99gift.ae"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <Box
                                component="img"
                                src="https://cdn-icons-png.flaticon.com/512/733/733547.png"
                                alt="Facebook"
                                sx={{ width: 22, height: 22, cursor: 'pointer' }}
                            />
                        </a>

                        {/* LinkedIn */}
                        <a
                            href="https://www.linkedin.com/company/99-gift"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none' }}
                        >
                            <Box
                                component="img"
                                src="https://cdn-icons-png.flaticon.com/512/174/174857.png"
                                alt="LinkedIn"
                                sx={{ width: 22, height: 22, cursor: 'pointer' }}
                            />
                        </a>
                    </Box>
                </Box>

                {/* Banner Image */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Box
                        component="img"
                        src={currentBlog?.blogBannerImage}
                        sx={{
                            width: '100%',
                            maxHeight: { xs: '250px', md: '350px' },
                            objectFit: 'cover',
                            borderRadius: '10px',
                            boxShadow: 2,
                        }}
                    />
                </Box>

                {/* Blog Content */}
                <Container maxWidth="xl"
                    sx={{
                        mt: 4, mb: 10,
                        '& #blog-title': {
                            display: 'none'
                        }
                    }}>
                    <Box dangerouslySetInnerHTML={{ __html: currentBlog?.content }} />
                </Container>
            </Container>
        </React.Fragment>
    );
};

export default Index;
